import { SubscriptionPlanType } from './subscription'
import { Dataset } from './dataset'

/**
 * Just one of the styling params for a felt layer
 */
interface LayerAttributes {
  displayName?: string
  format?: {
    mantissa?: number
    thousandSeparated?: boolean
  }
}

/**
 * Styles applied to a layer when it's clipped based on AOI of user
 */
interface LayerStyle {
  type: string
  label: {
    color: string
    justify: string
    maxZoom: number
    minZoom: number
    padding: number
    fontSize: number
    fontStyle: string
    haloColor: string
    haloWidth: number
    placement: string
    fontWeight: number
    lineHeight: number
    maxLineChars: number
    letterSpacing: number
    textTransform: string
  }
  paint: {
    color: string[]
    opacity: number
    strokeColor: string
    strokeWidth: number
    isSandwiched: boolean
  }
  popup: {
    keyAttributes: string[]
  }
  config: {
    showOther: boolean
    categories: {
      type: string
      count: number
    }
    labelAttribute: string[]
    categoricalAttribute: string
  }
  legend: {
    displayName: string
  }
  version: string
  attributes: Record<string, LayerAttributes>
}

export enum LayerUploadStatus {
  UPLOADING = 'UPLOADING',
  COMPLETED = 'COMPLETED',
  PROCESSING = 'PROCESSING',
  ERROR = 'ERROR',
  PENDING = 'PENDING',
  NEEDS_REVIEW = 'NEEDS_REVIEW',
  NO_DATA = 'NO_DATA',
}

export enum LayerType {
  VECTOR = 'VECTOR',
  RASTER = 'RASTER',
}

// Stands for "no value" in LayerFilters.countries and LayerFilters.datasetIds (LAYER_FILTER_NONE on the backend).
export const LAYER_FILTER_NONE = 'none'

export interface LayerFilters {
  countries: string[]
  datasetIds: string[]
  statuses: string[]
  tenantIds: string[]
}

export interface LayersResponse {
  layers: LayerItem[]
  total: number
  nextSkip: number | null
}

/**
 * System layer used for clipping based on AOI of user
 */
export interface LayerItem {
  id: string
  fileName: string
  containerName: string
  blobPath: string
  blobUrl: string
  mimeType: string
  status: LayerUploadStatus
  sizeBytes: number
  chunks: number // Always returns 1 since no chunking, just direct upload
  totalChunks: number // Always returns 1 since no chunking, just direct upload
  style: LayerStyle | null
  widgets: any
  srid: number | null
  xMin: number | null
  yMin: number | null
  xMax: number | null
  yMax: number | null
  errorMessage: string | null
  type: LayerType
  clipOutputType: LayerType
  createdAt: string
  updatedAt: string
  vectorAttributes: any[] // This could be refined if you have details about vectorAttributes structure
  globallyVisibleStatus: boolean
  description: string | null
  orderNumber: number | null

  isPublic: boolean
  tier: SubscriptionPlanType
  country: string | null
  resolutionMeters: number | null
  internalNotes: string | null
  datasetId: string | null
  dataset: Dataset | null
  allowedTenants: { tenantId: string }[]
  _count: { projectLayerUploads: number }
}

// Maps to PATCH /layers/:fileId (UpdateLayerBodyDto).
export interface UpdateLayerMetadataPayload {
  fileName?: string
  orderNumber?: number | null
  description?: string | null
  tier?: SubscriptionPlanType
  datasetId?: string | null
  country?: string | null
  resolutionMeters?: number | null
  internalNotes?: string | null
  clipOutputType?: LayerType
  isPublic?: boolean
  allowedTenantIds?: string[]
}
